import { Tooltip } from 'antd';
import { Map } from 'ol';
import { ProjectionLike } from 'ol/proj';
import { HTMLAttributes, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetImageMultimedia } from '../../core/api/useGetMultimedia';
import useVariantsLoader from '../../core/api/useLoadVariants';
import { gradientOpacity, singleColorOpacity } from '../../helpers/convertOpacity';
import { stringToUnit } from '../../helpers/stringToUnit';
import { MAX_FULLSCREEN_HEIGHT } from '../../model/constants/constants';
import { BorderDef } from '../../model/definitions/BorderDef';
import { ForecastWDElementDef } from '../../model/definitions/ForecastWDElementDef';
import { PositionControlDef } from '../../model/definitions/PositionControlDef';
import { TimeControlDef } from '../../model/definitions/TimeControlDef';
import { ValueTypeEnum } from '../../model/enums/ValueTypeEnum';
import { ActiveDef, addPointForBiasForecast } from '../../store/slices/active-slice';
import { RootState } from '../../store/store';
import ElementContainer from './ElementContainer';
import { displayPointValue } from './showPointDataValue';
import { removeDuplicateIds, transformPercentToAbsolute } from './utils';

interface ForecastElementProps extends HTMLAttributes<HTMLDivElement> {
  panelProps: ForecastWDElementDef;
  canvas: { cnvWidth?: number; cnvHeight?: number };
  disabled?: boolean;
  parentTime?: TimeControlDef[];
  inPoster?: boolean;
  posterId?: string;
  parentSize?: PositionControlDef;
  isMapOverlay?: boolean;
  mapId?: string;
  geoPosterId?: string;
  mapRef?: React.MutableRefObject<Map | null>;
  projectionCalc?: ProjectionLike;
  sceneId: string;
}
export const ForecastElement = ({
  canvas,
  disabled,
  panelProps,
  parentTime,
  posterId,
  inPoster,
  parentSize,
  isMapOverlay = false,
  mapId,
  geoPosterId,
  mapRef,
  projectionCalc,
  sceneId,
}: ForecastElementProps) => {
  const dispatch = useDispatch();
  const { activeAspectRatio, biasMode, biasedValues, addedBiasedValues, biasParam } =
    useSelector<RootState>((state) => state.active) as ActiveDef;

  const {
    fontFamily,
    fontVariantId,
    fontType,
    fontColor,
    fontAlignment,
    fontSize,
    displayUnitOfMeasurement,
    forecastWDSource,
    boxDef,
    timeControls,
    strokeWidth,
    strokeColor,
    textTransform,
  } = panelProps;
  const {
    valueType,
    value: elemValue,
    parameterType,
    unitOfMeasurement,
    originalValue,
    biasFilterAppliedOnValue,
  } = forecastWDSource;
  const {
    paddingBottom,
    paddingTop,
    paddingLeft,
    paddingRight,
    background,
    borderRight,
    borderTop,
    borderLeft,
    borderBottom,
  } = boxDef;
  function isForecast(obj: any): obj is ForecastWDElementDef {
    if (obj) return 'forecastWDSource' in obj;
    return false;
  }

  const borderString = (val: BorderDef) => val && `${val.width}px ${val.style} ${val.color}`;
  //const url = useMultimediaObjectUrl(elemValue?.versionId, false);
  const { data: url } = useGetImageMultimedia(elemValue?.versionId, false);
  const { data: urlOrig } = useGetImageMultimedia(originalValue?.versionId, false);
  const { cnvHeight } = canvas;
  const activeValue =
    Array.isArray(elemValue) && elemValue?.find((val) => val.unit === unitOfMeasurement);
  let fontUnit;
  if (inPoster && cnvHeight) {
    const cH = transformPercentToAbsolute(
      cnvHeight,
      activeAspectRatio,
      'height',
      MAX_FULLSCREEN_HEIGHT,
    );
    fontUnit = cH / 100;
  } else {
    fontUnit = (cnvHeight ?? MAX_FULLSCREEN_HEIGHT) / 100;
  }
  const disabledForBias =
    [...addedBiasedValues, ...biasedValues].length > 0 &&
    [...addedBiasedValues, ...biasedValues].every((item) => !isForecast(item));
  const { color } = background;
  const backgroundClip = fontColor?.includes('linear') ? 'text' : 'initial';
  const colorBgOpacity = color?.includes('linear-gradient')
    ? gradientOpacity(color)
    : singleColorOpacity(color);
  const bgColor = fontColor?.includes('linear-gradient')
    ? gradientOpacity(fontColor)
    : colorBgOpacity;
  useVariantsLoader({ name: fontFamily, fontVariantId: fontVariantId });
  const indexOfBias = biasedValues.findIndex((item) => item.id === panelProps.id);
  const indexOfConfirmed = removeDuplicateIds(addedBiasedValues).findIndex(
    (item) => item.id === panelProps.id,
  );
  const getBiasValue = () => {
    const element =
      removeDuplicateIds(addedBiasedValues)[indexOfConfirmed] || biasedValues[indexOfBias];
    if (isForecast(element)) return element.forecastWDSource.value;
  };
  const pointValue = useMemo(
    () =>
      displayPointValue(
        valueType,
        valueType === ValueTypeEnum.IMAGE
          ? elemValue?.versionId
          : !biasMode
          ? elemValue
          : getBiasValue() || elemValue,
        parameterType,
        elemValue?.jsonAnimation,
        url,
        unitOfMeasurement,
        false,
        elemValue?.originalZipSequence,
        elemValue,
        undefined,
        timeControls,
      ),
    [elemValue, parameterType, unitOfMeasurement, url, valueType, biasedValues, addedBiasedValues],
  );
  const tooltipContent = (
    <div>
      {displayPointValue(
        valueType,
        !biasMode && valueType === ValueTypeEnum.IMAGE
          ? originalValue?.versionId
          : getBiasValue() || elemValue,
        parameterType,
        originalValue?.jsonAnimation,
        urlOrig,
        unitOfMeasurement,
        true,
        elemValue?.originalZipSequence,
        elemValue,
        undefined,
        timeControls,
      )}
    </div>
  );
  return (
    <ElementContainer
      sceneId={sceneId}
      visibility={panelProps.enabled}
      canvas={canvas}
      lock={valueType === ValueTypeEnum.IMAGE}
      disabled={!!disabled || disabledForBias}
      type={'forecastWDElements'}
      panelProps={panelProps}
      style={{
        fontFamily: fontFamily + ' ' + fontType,
        textTransform,
        color: fontColor?.includes('linear') ? 'transparent' : singleColorOpacity(fontColor),
        textAlign: fontAlignment as FontAlignment,
        fontSize: fontUnit * fontSize,
        border: indexOfBias > -1 ? '4px dashed red' : 'inherit',
        opacity:
          (biasMode && biasParam !== panelProps.forecastWDSource.parameterType && biasParam) ||
          disabledForBias
            ? 0.3
            : 'inherit',
      }}
      parentTime={parentTime}
      inPoster={inPoster}
      posterId={posterId}
      parentSize={parentSize}
      isMapOverlay={isMapOverlay}
      parentMapId={mapId}
      geoPosterId={geoPosterId}
      onClick={() => biasMode && dispatch(addPointForBiasForecast({ point: panelProps }))}
    >
      <div
        key={fontColor + 'container'}
        style={{
          height: '100%',
          overflow: 'hidden',
          background: indexOfConfirmed >= 0 && biasMode ? 'green' : bgColor,
          paddingTop: fontUnit * paddingTop,
          paddingLeft: fontUnit * paddingLeft,
          paddingRight: fontUnit * paddingRight,
          paddingBottom: fontUnit * paddingBottom,
          borderLeft: borderString(borderLeft),
          borderRight: borderString(borderRight),
          borderTop: borderString(borderTop),
          borderBottom: borderString(borderBottom),
          textAlign: 'left',
          // @ts-ignore
          WebkitBackgroundClip: backgroundClip,
        }}
      >
        <div style={{ overflow: 'hidden', width: '100%', height: '100%' }}>
          {(biasFilterAppliedOnValue ||
            (activeValue && activeValue.value !== activeValue.originalValue)) && (
            <Tooltip title={tooltipContent}>
              <div
                className={'absolute top-0 right-0 !bg-red-800 w-5 h-5 z-40 !border-0'}
                style={{
                  overflow: 'hidden',
                  transform: 'skew(45deg)',
                  transformOrigin: 'top right',
                }}
              />
            </Tooltip>
          )}
          <div
            style={{
              height: '100%',
              //
              //marginTop: valueType !== ValueTypeEnum.IMAGE ? fontSize * 1.15 : 0,
              lineHeight: 1.15,
              // @ts-ignore
              WebkitBackgroundClip: backgroundClip,
              textAlign: fontAlignment as FontAlignment,
              WebkitTextStroke: strokeWidth
                ? `${strokeWidth}px ${singleColorOpacity(strokeColor)}`
                : '0px',
            }}
          >
            {pointValue}
            {displayUnitOfMeasurement && <span>{stringToUnit(unitOfMeasurement)}</span>}
          </div>
        </div>
      </div>
    </ElementContainer>
  );
};
